import { useEffect, useRef } from "react";
import type { UseFormReturn } from "react-hook-form";
import { track } from "common/utils/mixpanel/tracker";
import type { ArtworkForm } from "@design-lab/shared/types";
import { usePrevious } from "./usePrevious";

type UseWebSocketTrackingProps = {
  isConnected: boolean;
  artworkForm: UseFormReturn<ArtworkForm> | undefined;
};

export function useWebSocketTracking({ isConnected, artworkForm }: UseWebSocketTrackingProps) {
  const previousIsConnected = usePrevious(isConnected);
  const hasTrackedInitialConnection = useRef(false);

  useEffect(() => {
    if (!artworkForm) {
      return;
    }

    if (!hasTrackedInitialConnection.current && isConnected) {
      hasTrackedInitialConnection.current = true;
      return;
    }

    if (previousIsConnected === undefined) return;

    const formValues = artworkForm.getValues();
    const frontArtworkId = formValues?.["front"]?.artworkId || null;
    const backArtworkId = formValues?.["back"]?.artworkId || null;

    if (previousIsConnected && !isConnected) {
      if (frontArtworkId) {
        track("websocket_connection_lost", { artwork_id: frontArtworkId, side: "front" });
      }
      if (backArtworkId) {
        track("websocket_connection_lost", { artwork_id: backArtworkId, side: "back" });
      }
      if (!frontArtworkId && !backArtworkId) {
        track("websocket_connection_lost", { artwork_id: null });
      }
    }

    if (!previousIsConnected && isConnected) {
      if (frontArtworkId) {
        track("websocket_connection_reestablished", {
          artwork_id: frontArtworkId,
          side: "front"
        });
      }
      if (backArtworkId) {
        track("websocket_connection_reestablished", { artwork_id: backArtworkId, side: "back" });
      }
      if (!frontArtworkId && !backArtworkId) {
        track("websocket_connection_reestablished", { artwork_id: null });
      }
    }
  }, [isConnected, previousIsConnected, artworkForm]);
}
